/**
 * TEMP-H-004: Stage Settings Hook (GET)
 * 
 * Fetches stage settings and persona thresholds with permission checking.
 * Integrates with existing auth patterns and provides caching.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/lib/validated-api-client';
import type {
  StageSettingsConfig,
  PersonaThreshold,
  SettingsPermissionCheck
} from '@/types/stage-settings';
import { stageSettingsKeys } from '@/types/stage-settings';
import type { StageName } from '@/lib/shared-types';
import { isValidStage } from '@/lib/type-guards';

// Legacy type alias for backward compatibility
type StageType = StageName;
import { joinApiPath } from '@/lib/api-config';
import { getApiHeaders } from '@/lib/api-base';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { stageAPI } from '@/services/stage-api';

/**
 * Transform backend stage data to settings config format
 */
export function transformToSettingsConfig(
  executionId: string,
  stage: StageType,
  backendData: any,
  permissions: SettingsPermissionCheck
): StageSettingsConfig {
  const personas: PersonaThreshold[] = (backendData.availablePersonas || []).map((persona: any) => ({
    personaSlug: persona.personaSlug,
    personaName: stageAPI.getPersonaInfo(persona.personaSlug).name,
    personaRole: stageAPI.getPersonaInfo(persona.personaSlug).role,
    passMin: persona.passMin,
    fallbackMin: persona.fallbackMin,
    categoryThresholds: persona.categoryThresholds || {},
    categoryWeights: persona.categoryWeights || {},
    retryLimit: persona.retryLimit || 3,
    version: persona.version || 'v1',
    hash: persona.hash || 'unknown'
  }));

  const activePersona = personas.find(p => p.personaSlug === backendData.activePersonaSlug) 
    || personas[0];

  return {
    executionId,
    stage,
    isEditable: permissions.canEdit && backendData.isEditable !== false,
    overriddenFromLaunch: backendData.overriddenFromLaunch || false,
    activePersona,
    availablePersonas: personas,
    version: backendData.version || 'v1',
    updatedAt: backendData.updatedAt || new Date().toISOString(),
    updatedBy: backendData.updatedBy,
    launchDefaults: backendData.launchDefaults ? {
      personaSlug: backendData.launchDefaults.personaSlug,
      personaName: stageAPI.getPersonaInfo(backendData.launchDefaults.personaSlug).name,
      personaRole: stageAPI.getPersonaInfo(backendData.launchDefaults.personaSlug).role,
      passMin: backendData.launchDefaults.passMin,
      fallbackMin: backendData.launchDefaults.fallbackMin,
      categoryThresholds: backendData.launchDefaults.categoryThresholds || {},
      categoryWeights: backendData.launchDefaults.categoryWeights || {},
      retryLimit: backendData.launchDefaults.retryLimit || 3,
      version: backendData.launchDefaults.version || 'v1',
      hash: backendData.launchDefaults.hash || 'launch-default'
    } : undefined
  };
}

/**
 * Transform basic backend stage data to minimal settings config for list view
 * This is used when we don't have full persona data, just basic stage info
 */
export function transformBasicStageData(executionId: string, backendStage: any): StageSettingsConfig {
  // Create a minimal persona from the stage settings
  const activePersona: PersonaThreshold = {
    personaSlug: backendStage.qcPersonaSlug || 'unknown',
    personaName: backendStage.qcPersonaSlug ? stageAPI.getPersonaInfo(backendStage.qcPersonaSlug).name : 'Unknown',
    personaRole: backendStage.qcPersonaSlug ? stageAPI.getPersonaInfo(backendStage.qcPersonaSlug).role : 'Unknown',
    passMin: backendStage.passMin || 0.7,
    fallbackMin: backendStage.fallbackMin || 0.5,
    categoryThresholds: backendStage.categoryThresholds || {},
    categoryWeights: backendStage.categoryWeights || {},
    retryLimit: backendStage.retryLimit || 3,
    version: backendStage.version || 'v1',
    hash: 'basic-transform'
  };

  return {
    executionId,
    stage: backendStage.stage as StageType,
    isEditable: !backendStage.isDefaultFromLaunch,
    overriddenFromLaunch: !backendStage.isDefaultFromLaunch,
    activePersona,
    availablePersonas: [activePersona], // Only one available for list view
    version: backendStage.version || 'v1',
    updatedAt: backendStage.updatedAt || new Date().toISOString(),
    updatedBy: backendStage.updatedBy,
    launchDefaults: undefined // Not available in list view
  };
}

/**
 * Check user permissions for stage settings
 */
function useSettingsPermissions(
  _executionId: string,
  _stage: StageType,
  executionStatus?: string
): SettingsPermissionCheck {
  const { isAdmin, isAuthenticated } = useServerAuth();

  return useMemo(() => {
    if (!isAuthenticated) {
      return {
        canView: false,
        canEdit: false,
        canEditAdvanced: false,
        canSwitchPersona: false,
        canViewAuditLog: false,
        effectiveRole: 'read'
      };
    }

    // Admin can do everything
    if (isAdmin) {
      return {
        canView: true,
        canEdit: true,
        canEditAdvanced: true,
        canSwitchPersona: true,
        canViewAuditLog: true,
        effectiveRole: 'admin'
      };
    }

    // Regular users can view and edit basic settings
    // But cannot edit advanced settings or switch personas
    const canEdit = executionStatus !== 'completed' && executionStatus !== 'cancelled';
    
    return {
      canView: true,
      canEdit,
      canEditAdvanced: false,
      canSwitchPersona: false,
      canViewAuditLog: false,
      effectiveRole: canEdit ? 'edit' : 'read'
    };
  }, [isAuthenticated, isAdmin, executionStatus]);
}

/**
 * Hook to fetch stage settings and persona thresholds
 */
export function useStageSettings(executionId: string, stage: StageType) {
  const { isAuthenticated } = useServerAuth();
  
  // Get execution status for permission checks
  // This would normally come from execution store, but we'll fetch it separately
  const executionStatusQuery = useQuery({
    queryKey: ['execution', executionId, 'status'],
    queryFn: async () => {
      const response = await fetch(joinApiPath('executions', executionId), {
        credentials: 'include',
        headers: getApiHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch execution: ${response.status}`);
      }
      const data = await response.json();
      return data?.execution?.status ?? 'running';
    },
    enabled: !!executionId && isAuthenticated,
    staleTime: 30 * 1000,
    retry: 2
  });

  const permissions = useSettingsPermissions(executionId, stage, executionStatusQuery.data);

  // Main settings query
  const settingsQuery = useQuery({
    queryKey: stageSettingsKeys.stage(executionId, stage),
    queryFn: async (): Promise<StageSettingsConfig> => {
      const response = await fetch(
        joinApiPath('executions', executionId, 'stages', stage, 'settings'),
        {
          credentials: 'include',
          headers: getApiHeaders(),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`
        }));

        let errorMessage = error.message || 'Failed to fetch stage settings';
        
        if (response.status === 401) {
          errorMessage = 'Authentication required to access stage settings';
        } else if (response.status === 403) {
          errorMessage = 'You do not have permission to view stage settings';
        } else if (response.status === 404) {
          errorMessage = 'Stage settings not found';
        }

        throw new Error(errorMessage);
      }

      const backendData = await response.json();
      return transformToSettingsConfig(executionId, stage, backendData, permissions);
    },
    enabled: !!executionId && !!stage && permissions.canView,
    staleTime: 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      // Don't retry permission errors
      if (error?.message?.includes('permission') || error?.message?.includes('Authentication')) {
        return false;
      }
      return failureCount < 3;
    }
  });

  // Personas query for dropdown/selection
  const personasQuery = useQuery({
    queryKey: stageSettingsKeys.personas(executionId, stage),
    queryFn: async () => {
      const response = await fetch(
        joinApiPath('executions', executionId, 'stages', stage, 'personas'),
        {
          credentials: 'include',
          headers: getApiHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch personas: ${response.status}`);
      }

      const data = await response.json();
      return data.personas || [];
    },
    enabled: !!executionId && !!stage && permissions.canSwitchPersona,
    staleTime: 5 * 60 * 1000, // 5 minutes (personas change rarely)
  });

  // Computed values
  const hasOverrides = useMemo(() => {
    if (!settingsQuery.data?.launchDefaults || !settingsQuery.data?.activePersona) {
      return false;
    }

    const current = settingsQuery.data.activePersona;
    const defaults = settingsQuery.data.launchDefaults;

    return (
      current.passMin !== defaults.passMin ||
      current.fallbackMin !== defaults.fallbackMin ||
      current.retryLimit !== defaults.retryLimit ||
      JSON.stringify(current.categoryThresholds) !== JSON.stringify(defaults.categoryThresholds) ||
      JSON.stringify(current.categoryWeights) !== JSON.stringify(defaults.categoryWeights)
    );
  }, [settingsQuery.data]);

  return {
    // Data
    settings: settingsQuery.data,
    availablePersonas: personasQuery.data,
    
    // Loading states
    isLoading: settingsQuery.isLoading || executionStatusQuery.isLoading,
    isError: settingsQuery.isError,
    error: settingsQuery.error,
    
    // Permissions
    permissions,
    
    // Computed state
    hasOverrides,
    isEditable: settingsQuery.data?.isEditable || false,
    
    // Actions
    refetch: settingsQuery.refetch,
    refetchPersonas: personasQuery.refetch,
  };
}

/**
 * Hook to fetch all stage settings for execution overview
 */
export function useAllStageSettings(executionId: string) {
  const { isAuthenticated } = useServerAuth();

  return useQuery({
    queryKey: stageSettingsKeys.execution(executionId),
    queryFn: async () => {
      // Use validated API client for type-safe response
      const executionSettings = await apiClient.getExecutionSettings(executionId);
      
      // Transform array to record keyed by stage name
      const stagesRecord: Record<string, any> = {};
      executionSettings.stages.forEach((stage: any) => {
        if (stage.stage && isValidStage(stage.stage)) {
          stagesRecord[stage.stage] = stage;
        }
      });
      
      return stagesRecord;
    },
    enabled: !!executionId && isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    retry: 2
  });
}